import React, { useState, useEffect } from 'react'
import { getValidUserAccessData, saveUserAccessData, isUserAccessValid, getRemainingAccessTime } from '@/utils/userAccess'
import { emailService } from '@/services/emailService'

interface DownloadFormProps {
  isOpen: boolean
  onClose: () => void
  paperTitle: string
  pdfPath: string
}

const DownloadForm: React.FC<DownloadFormProps> = ({ isOpen, onClose, paperTitle, pdfPath }) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [org, setOrg] = useState('')
  const [phone, setPhone] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [hasValidAccess, setHasValidAccess] = useState(false)
  const [remainingTime, setRemainingTime] = useState<number>(0)

  // Check for valid user access when component mounts or opens
  useEffect(() => {
    if (isOpen) {
      const validAccess = isUserAccessValid()
      setHasValidAccess(validAccess)
      
      if (validAccess) {
        const userData = getValidUserAccessData()
        if (userData) {
          setName(userData.name)
          setEmail(userData.email)
          setOrg(userData.organization)
          setPhone(userData.phone || "")
          setSubmitted(true) // Skip form for valid users
        }
        
        // Set remaining time
        const remaining = getRemainingAccessTime()
        setRemainingTime(remaining)
      } else {
        // Reset form for new users
        setSubmitted(false)
      }
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !email || !org) return
    
    try {
      // Send to email service
      const result = await emailService.sendDownloadForm({
        name,
        email,
        organization: org,
        phone: phone || undefined,
        paperTitle,
        pdfPath
      }, `white_paper_download_${paperTitle.toLowerCase().replace(/\s+/g, '_')}`)
      
      if (result.success) {
        
        // Save user data to cookie
        saveUserAccessData({
          name,
          email,
          organization: org,
          phone: phone || undefined
        })
        
        setSubmitted(true)
        setHasValidAccess(true)
        setRemainingTime(10) // Set to full 10 hours after new submission
      } else {
        console.error('Failed to send download form:', result.message)
        // Still allow download but log the error
        saveUserAccessData({
          name,
          email,
          organization: org,
          phone: phone || undefined
        })
        
        setSubmitted(true)
        setHasValidAccess(true)
        setRemainingTime(10)
      }
    } catch (error) {
      console.error('Error submitting download form:', error)
      // Fallback: still allow download
      saveUserAccessData({
        name,
        email,
        organization: org,
        phone: phone || undefined
      })
      
      setSubmitted(true)
      setHasValidAccess(true)
      setRemainingTime(10)
    }
  }

  const handleDownload = () => {
    // Open PDF in a new tab instead of downloading
    window.open(pdfPath, '_blank', 'noopener,noreferrer')
  }

  const handleClose = () => {
    setName('')
    setEmail('')
    setOrg('')
    setPhone('')
    setSubmitted(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl p-8">
        <h2 className="text-2xl font-bold mb-2 text-slate-900">Download {paperTitle}</h2>
        <p className="text-slate-600 mb-6">
          {hasValidAccess 
            ? `Welcome back! You have access to ${paperTitle}. Your session expires in ${Math.round(remainingTime)} hours.`
            : `Please provide your information to download the white paper: ${paperTitle}.`
          }
        </p>
        
        {!submitted && !hasValidAccess ? (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Full Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              type="email"
              placeholder="Email Address *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Organization *"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              required
            />
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              type="tel"
              placeholder="Phone Number (Optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 hover:bg-indigo-700"
              >
                Submit & Download
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-green-800 text-sm">
              ✅ Thank you! Your information has been submitted successfully.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button 
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 hover:bg-indigo-700"
                onClick={handleDownload}
              >
                📄 View PDF
              </button>
            </div>
            <button
              className="mt-4 text-slate-500 hover:text-slate-700 text-sm"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DownloadForm
